import { ActivityType, UserWorkoutSession } from 'fitbeat.gym-core';
import { CardioOrResistance } from 'fitbeat.models';

export enum AuthorizationStatus {
  notDetermined = 'notDetermined',
  denied = 'denied',
  authorized = 'authorized',
  serviceUnavailable = 'serviceUnavailable',
}

export type UnRegisterEventListener = () => void;
export type HREventListener = (heartRate: number) => void;

export interface IPrepareHomeWorkout {
  userId: string;
  activityType: ActivityType;
}

export interface IPrepareStudioWorkout {
  userId: string;
  activityType: ActivityType;
  hrMetricsServerBaseUrl: string;
  isoStartTimeOfWarmup: string;
  isoEndTime: string;
  startingZone: string;
}

export type SupportedConnectedApp = 'Apple Health' | 'Garmin Connect' | 'Health Connect' | 'Mock';

export interface IAuthorizationResult {
  success: boolean;
  errorDisplayMessage?: string;
  urlRedirect?: string;
}

export interface IOnboardable {
  isOnboardable: true;
  isAppInstalled: boolean;
  isConnectedDeviceAvailable?: boolean;
  isAppInstalledOnConnectedDevice?: boolean;
}

export interface INotOnboardable {
  isOnboardable: false;
}

// todo: try to split this into a ConnectedWorkoutApp and ConnectedRealTimeWorkoutApp
abstract class ConnectedWorkoutApp {
  public readonly name: SupportedConnectedApp;

  public constructor(name: SupportedConnectedApp) {
    this.name = name;
  }

  public async debugTestIntegration(): Promise<void> {
    // no implementation by default
  }

  public abstract isOnboardable(): Promise<IOnboardable | INotOnboardable>;

  public abstract isAvailable(): Promise<boolean>;

  public initialize(): Promise<void> {
    // no implementation by default
    return Promise.resolve();
  }

  public abstract requestAuthorization(): Promise<IAuthorizationResult>;

  public abstract authorizationStatus(): Promise<AuthorizationStatus>;

  /**
   * Stops a real-time workout, or saves a post-workout data
   */
  public abstract stopAndSaveWorkout(workoutSession: UserWorkoutSession): Promise<string | void>;

  /**
   * True if a HR monitor provided directly from the ConnectedWorkoutApp is set up (ex an Apple Watch)
   */
  public abstract isAppInstalledOnConnectedDevice(): Promise<boolean>;

  /**
   * True if health data is available from the ConnectedWorkoutApp (ex from an Apple Watch)
   */
  public abstract isConnectedDeviceAvailable(): Promise<boolean>;

  // real time ConnectedWorkoutApp details

  public abstract canUseAsRealtimeHeartRateMonitor(): Promise<boolean>;

  public abstract registerHREventHandler(listener: HREventListener): UnRegisterEventListener;

  public abstract prepareHomeWorkout(options: IPrepareHomeWorkout): Promise<void>;

  public abstract prepareStudioWorkout(options: IPrepareStudioWorkout): Promise<void>;

  public abstract startWorkout(): Promise<void>;

  public abstract stopAndDiscardWorkout(): Promise<void>;

  public abstract pauseWorkout(): Promise<void>;

  public abstract resumeWorkout(): Promise<void>;
}

export default ConnectedWorkoutApp;
